import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import { Box, Stack, styled, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCallback } from 'react';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { SmsLiveDot } from 'src/pages/Conversations/Chat/SmsSession/sms-live-dot';

const SwitchContainer = styled(Box, { name: 'SmsChannelSwitchContainer' })(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.125rem',
  padding: '0.125rem',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.text.primary, 0.05),
  flexShrink: 0,
}));

const SwitchTab = styled('button', {
  name: 'SmsChannelSwitchTab',
  shouldForwardProp: (p) => p !== 'active',
})<{ active?: boolean }>(({ theme, active, disabled }) => {
  const inactiveColor = disabled
    ? theme.palette.action.disabled
    : theme.palette.text.secondary;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 1.25),
    borderRadius: `calc(${theme.shape.borderRadius}px - 2px)`,
    border: `1px solid ${active ? alpha(theme.palette.text.primary, 0.2) : 'transparent'}`,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit',
    fontSize: theme.typography.body2.fontSize,
    fontWeight: active
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
    color: active ? theme.palette.text.primary : inactiveColor,
    backgroundColor: active ? theme.palette.background.paper : 'transparent',
    transition: 'background-color 0.15s, color 0.15s, border-color 0.15s',
    '&:hover:not(:disabled)': {
      backgroundColor: active ? theme.palette.background.paper : theme.palette.action.hover,
    },
  };
});

export type SmsChannelMode = 'email' | 'sms';

interface SmsChannelSwitchProps {
  mode: SmsChannelMode;
  onChange: (mode: SmsChannelMode) => void;
  smsEnabled: boolean;
  /** Tooltip shown on the inert SMS tab — the deliverability reason (e.g. landline). */
  smsDisabledReason?: string | null;
}

export const SmsChannelSwitch = ({
  mode,
  onChange,
  smsEnabled,
  smsDisabledReason,
}: SmsChannelSwitchProps) => {
  const handleEmail = useCallback(() => {
    if (mode !== 'email') onChange('email');
  }, [mode, onChange]);

  const handleSms = useCallback(() => {
    if (!smsEnabled || mode === 'sms') return;
    onChange('sms');
  }, [mode, onChange, smsEnabled]);

  return (
    <SwitchContainer>
      <SwitchTab active={mode === 'email'} onClick={handleEmail} type="button">
        <MailOutlineIcon sx={{ fontSize: '1rem' }} />
        Email
      </SwitchTab>
      <Tooltip title={smsDisabledReason ?? ''} disableHoverListener={smsEnabled}>
        <span>
          <SwitchTab
            active={mode === 'sms'}
            disabled={!smsEnabled}
            onClick={handleSms}
            type="button"
          >
            <SmsOutlinedIcon sx={{ fontSize: '1rem' }} />
            SMS
          </SwitchTab>
        </span>
      </Tooltip>
    </SwitchContainer>
  );
};

export const SmsLiveRecipientLine = ({
  clientFirstName,
  hasOpenSession,
}: {
  clientFirstName?: string;
  /** Live session open → pulsing dot + live copy; none → sending starts one. */
  hasOpenSession: boolean;
}) => (
  <Stack direction="row" alignItems="center" gap={0.75} sx={{ minHeight: '1.75rem' }}>
    <Typography variant="body2" color="text.secondary">
      To
    </Typography>
    {hasOpenSession ? <SmsLiveDot sx={{ width: '0.4375rem', height: '0.4375rem' }} /> : null}
    <Typography
      variant="body2"
      fontWeight={500}
      noWrap
      sx={{ color: (theme) => theme.palette.custom.OV.entity.conversation }}
    >
      {hasOpenSession
        ? `texting ${clientFirstName ?? 'client'} · live SMS session`
        : `texting ${clientFirstName ?? 'client'} · sending starts a new SMS session`}
    </Typography>
  </Stack>
);
